/**
 * The pieces broken off in crashes, lying in the road.
 *
 * Each is its own mesh, moved by `core/vehicle/debris.ts` until it comes to
 * rest, then left where it lies. They are kept in scene metres and moved
 * with the scene when its origin moves (`shift`). The oldest go once there
 * are too many, and any far behind the player.
 */

import type { Mesh } from 'three';
import { Group } from 'three';
import type { Ground, Piece } from '../core/vehicle/debris.js';
import { stepPiece } from '../core/vehicle/debris.js';
import type { Vec3 } from '../core/vehicle/damage.js';
import type { BrokenPiece } from './crumple.js';

/** Most pieces kept lying about. */
const MOST = 80;
/** Further than this from the player, metres, a piece is let go. */
const FAR = 350;

export class Debris {
  readonly group = new Group();
  private readonly pieces: Array<{ mesh: Mesh; body: Piece }> = [];

  get count(): number {
    return this.pieces.length;
  }

  /**
   * Throw a broken piece, from where it is, at `velocity` m/s and spinning at
   * `spin` rad/s; or with `fixed`, leave it standing where it is, as what is
   * left of a bus shelter's frame does.
   */
  add(piece: BrokenPiece, velocity: Vec3, spin: Vec3, fixed = false): void {
    const { mesh } = piece;
    const q = mesh.quaternion;
    this.group.add(mesh);
    this.pieces.push({
      mesh,
      body: {
        p: [mesh.position.x, mesh.position.y, mesh.position.z], q: [q.x, q.y, q.z, q.w],
        v: [...velocity], w: [...spin], half: piece.half, resting: fixed, still: 0,
      },
    });
    while (this.pieces.length > MOST) this.remove(0);
  }

  /**
   * Pick up the piece lying nearest scene (x, y, z) within `reach` metres:
   * it is no longer moved here, but stays drawn, for whoever has it to move.
   */
  takeNear(x: number, y: number, z: number, reach: number): BrokenPiece | undefined {
    let best = -1, bestD = reach;
    this.pieces.forEach(({ body }, i) => {
      const d = Math.hypot(body.p[0] - x, (body.p[1] - y) * 0.5, body.p[2] - z);
      if (d < bestD) { bestD = d; best = i; }
    });
    if (best < 0) return undefined;
    const [{ mesh, body }] = this.pieces.splice(best, 1);
    return { mesh, half: body.half };
  }

  /**
   * Move every piece on by `dt` over `ground`. `shift` is how far the scene's
   * origin moved since last frame; (px, pz) is the player.
   */
  update(dt: number, ground: Ground, shift: { x: number; z: number }, px: number, pz: number): void {
    for (let i = this.pieces.length - 1; i >= 0; i--) {
      const { mesh, body } = this.pieces[i];
      body.p[0] += shift.x;
      body.p[2] += shift.z;
      if (Math.hypot(body.p[0] - px, body.p[2] - pz) > FAR) {
        this.remove(i);
        continue;
      }
      stepPiece(body, dt, ground);
      mesh.position.set(body.p[0], body.p[1], body.p[2]);
      mesh.quaternion.set(body.q[0], body.q[1], body.q[2], body.q[3]);
    }
  }

  private remove(i: number): void {
    const { mesh } = this.pieces[i];
    this.group.remove(mesh);
    mesh.geometry.dispose();
    this.pieces.splice(i, 1);
  }
}
